import React, { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, Trash2, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import JobsRepository from "../repositories/JobsRepository";
import ConfirmationModal from "../components/common/ConfirmationModal";
import { UserRole } from "../models/User";
import SqlScriptJobsPanel from "../components/Jobs/SqlScriptJobsPanel";
import { QUERY_KEYS } from "../constants/QueryKeys";
import { MigrationType, MigrationTypeLabels } from "../models/Statistics/MigrationType";
import {
    RECALCULATION_MIGRATION_TYPES,
    RECALCULATION_WARNINGS,
} from "../constants/RecalculationWarnings";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface ExecuteSqlScriptsPageProps {
    userRole: UserRole;
    organizationName?: string;
    organizationId?: string;
}

const ExecuteSqlScriptsPage: React.FC<ExecuteSqlScriptsPageProps> = ({
    userRole,
    organizationName,
    organizationId,
}) => {
    const [sqlScript, setSqlScript] = useState<string>("");
    const [reason, setReason] = useState<string>("");
    const [fileName, setFileName] = useState<string | null>(null);
    const [followUpTypes, setFollowUpTypes] = useState<MigrationType[]>([]);
    const [status, setStatus] = useState<
        "idle" | "loading" | "submitted" | "error"
    >("idle");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] =
        useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const jobsRepository = new JobsRepository();
    const queryClient = useQueryClient();

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith(".sql")) {
            setSqlScript("");
            setFileName(null);
            setStatus("error");
            setErrorMessage("Please upload a valid .SQL file.");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setSqlScript("");
            setFileName(null);
            setStatus("error");
            setErrorMessage("File size exceeds 5 MB limit.");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result;
            if (typeof content === "string") {
                setSqlScript(content);
                setFileName(file.name);
                setStatus("idle");
                setErrorMessage(null);
            }
        };
        reader.onerror = () => {
            setSqlScript("");
            setFileName(null);
            setStatus("error");
            setErrorMessage("Failed to read the file.");
        };
        reader.readAsText(file);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async () => {
        setStatus("loading");
        setErrorMessage(null);

        try {
            await jobsRepository.executeSqlScript(
                organizationId!,
                sqlScript,
                reason,
                followUpTypes
            );
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SQL_SCRIPT_JOBS, organizationId],
            });

            setStatus("submitted");
            setReason("");
            setSqlScript("");
            setFileName(null);
            setFollowUpTypes([]);
        } catch (error: any) {
            setStatus("error");
            setErrorMessage(error.message);
        }
    };

    const handleClearScript = () => {
        setSqlScript("");
        setFileName(null);
        setErrorMessage(null);
        setStatus("idle");
    };

    const isReasonValid = reason.trim().length >= 10;
    const canExecute =
        sqlScript.trim().length > 0 &&
        isReasonValid &&
        !!organizationId &&
        status !== "loading" &&
        (userRole === UserRole.Admin || userRole === UserRole.Editor);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <div>
                <div className="flex mt-2 mb-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between">
                    <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        Execute SQL Scripts
                        <DocsHelpLink to="how-to/run-sql-scripts" />
                    </h2>
                    <div className="flex gap-2">
                        <input
                            type="file"
                            accept=".sql"
                            className="hidden"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                        />
                        <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Upload className="size-4" />
                            Upload SQL File
                        </Button>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        className="text-destructive hover:text-destructive"
                                        onClick={() => handleClearScript()}
                                        disabled={!sqlScript}
                                    >
                                        <Trash2 className="size-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Clear script</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>

                <div className="mb-4 space-y-1.5">
                    <Label
                        htmlFor="execution-reason"
                        className="after:content-['*'] after:ml-0.5 after:text-destructive"
                    >
                        Execution Reason
                    </Label>
                    <Input
                        id="execution-reason"
                        placeholder="Provide a reason for executing this script (min 10 characters)..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        aria-invalid={reason.length > 0 && !isReasonValid}
                    />
                    <p
                        className={`text-xs ${
                            reason.length > 0 && !isReasonValid
                                ? "text-destructive"
                                : "text-muted-foreground"
                        }`}
                    >
                        {reason.length > 0 && !isReasonValid
                            ? "Reason must be at least 10 characters long."
                            : "Describe why this script needs to be executed. This will be logged for audit purposes."}
                    </p>
                </div>

                <div className="mb-4 space-y-1.5">
                    <Label>Post-execution actions</Label>
                    <ToggleGroup
                        type="multiple"
                        variant="outline"
                        size="sm"
                        value={followUpTypes}
                        onValueChange={(value) =>
                            setFollowUpTypes(value as MigrationType[])
                        }
                        className="w-full my-1 flex-wrap h-auto"
                    >
                        {RECALCULATION_MIGRATION_TYPES.map((type) => (
                            <ToggleGroupItem
                                key={type}
                                value={type}
                                className="flex-1 min-w-fit"
                            >
                                {MigrationTypeLabels[type]}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                    <p className="text-xs text-muted-foreground">
                        Refresh the files migration queue after the script
                        commits, so files are migrated in the correct order.
                        Use this if your script changes the status of files or
                        their parent entities (Cases, Intakes, Providers,
                        Templates, Firm Documents).
                    </p>
                </div>

                <Textarea
                    rows={20}
                    placeholder="Enter your PostgreSQL script here or upload a .SQL file..."
                    value={sqlScript}
                    onChange={(event) => {
                        setSqlScript(event.target.value);
                        setFileName(null);
                        if (status === "submitted") setStatus("idle");
                    }}
                    className="mb-4 font-mono field-sizing-fixed"
                />

                <div className="flex justify-center">
                    <Button
                        onClick={() => setIsConfirmModalOpen(true)}
                        disabled={!canExecute}
                    >
                        {status === "loading" && (
                            <Loader2 className="size-4 animate-spin" />
                        )}
                        Execute Script
                    </Button>
                </div>

                {status === "submitted" && (
                    <Alert className="mt-4 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30">
                        <CheckCircle2 className="size-4 text-emerald-600" />
                        <AlertDescription>
                            Script queued. It runs in the background; follow it
                            in the Jobs panel. Any follow-up refresh starts only
                            after the script commits.
                        </AlertDescription>
                    </Alert>
                )}

                {status === "error" && errorMessage && (
                    <Alert variant="destructive" className="mt-4">
                        <AlertTriangle className="size-4" />
                        <AlertDescription className="whitespace-pre-line">
                            {errorMessage}
                        </AlertDescription>
                    </Alert>
                )}
            </div>

            <div className="lg:h-[calc(100vh-100px)] flex flex-col">
                <SqlScriptJobsPanel organizationId={organizationId} />
            </div>

            {isConfirmModalOpen && (
                <ConfirmationModal
                    open={isConfirmModalOpen}
                    onClose={() => setIsConfirmModalOpen(false)}
                    onConfirm={async () => {
                        setIsConfirmModalOpen(false);
                        await handleSubmit();
                    }}
                    message="Are you sure you want to execute this SQL script? This action cannot be undone and might affect the database directly."
                    statusColor="danger"
                    buttonText="Execute"
                >
                    <div className="mt-4 flex flex-col gap-3">
                        <div>
                            <p className="text-sm font-semibold">
                                Target Organization Name:
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {organizationName || "Unknown"}
                            </p>
                        </div>

                        <div>
                            <p className="text-sm font-semibold">
                                Target Organization Id:
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {organizationId || "Unknown"}
                            </p>
                        </div>

                        <div>
                            <p className="text-sm font-semibold">
                                Script Source:
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {fileName
                                    ? `Uploaded file: ${fileName}`
                                    : "Manual input"}
                            </p>
                        </div>

                        <div>
                            <p className="text-sm font-semibold">
                                Execution Reason:
                            </p>
                            <div className="p-2 bg-muted rounded-sm border font-mono text-xs whitespace-pre-wrap max-h-24 overflow-auto">
                                {reason}
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-semibold">
                                SQL Preview:
                            </p>
                            <div className="p-2 bg-muted rounded-sm border font-mono text-xs whitespace-pre-wrap max-h-24 overflow-auto">
                                {sqlScript.substring(0, 100)}
                                {sqlScript.length > 100 ? "..." : ""}
                            </div>
                        </div>

                        {followUpTypes.length > 0 && (
                            <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
                                <p className="text-sm font-semibold flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                                    <AlertTriangle className="size-4" />
                                    Follow-up actions
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    After the script commits, the files
                                    migration queue will be refreshed for:{" "}
                                    <span className="font-medium text-foreground">
                                        {followUpTypes
                                            .map((t) => MigrationTypeLabels[t])
                                            .join(", ")}
                                    </span>
                                </p>
                                <ul className="mt-1.5 list-disc pl-5 text-xs text-muted-foreground space-y-1">
                                    {RECALCULATION_WARNINGS.map((warning, i) => (
                                        <li key={i}>{warning}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </ConfirmationModal>
            )}
        </div>
    );
};

export default ExecuteSqlScriptsPage;
